import gql from 'graphql-tag';
import { useCallback } from 'react';
import { fg } from '@atlaskit/platform-feature-flags';
import { useMutation, type MutationHookOptions } from '@apollo/react-hooks';
import { useMemo } from 'react';
import { createAtlAttributionHeader } from '../../utils';

interface MutationVariables {
	appId: string;
	contextAri: string;
	channel: string;
	eventName?: string;
	payload: string;
}

interface MutationData {
	ecosystem: {
		publishAppClientEvent: {
			statusCode: number;
		};
	};
}

export type PublishClientEventPayload = {
	data: {
		channel: string;
		eventName?: string;
		eventPayload: string;
	};
};

export const publishAppClientEvent = gql`
	mutation publishAppClientEvent(
		$appId: ID!
		$contextAri: ID!
		$channel: String!
		$eventName: String
		$payload: String!
	) {
		ecosystem {
			publishAppClientEvent(
				appId: $appId
				contextAri: $contextAri
				channel: $channel
				eventName: $eventName
				payload: $payload
			) {
				statusCode
			}
		}
	}
`;

export const usePublishClientEventsBridge: Function = (
	appId: string,
	contextAri: string,
	options?: MutationHookOptions<MutationData, MutationVariables>,
) => {
	const [mutationFunction] = useMutation<MutationData, MutationVariables>(
		publishAppClientEvent,
		options,
	);

	const headers = useMemo(() => {
		const initHeaders: Record<string, string> = {};
		// eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
		if (fg('forge-ui-include-atl-attribution-header')) {
			initHeaders['atl-attribution'] = createAtlAttributionHeader();
		}
		return initHeaders;
	}, []);

	return useCallback(
		(payload: PublishClientEventPayload, contextToken?: string) => {
			const { channel, eventName, eventPayload } = payload.data;
			return mutationFunction({
				variables: {
					appId,
					contextAri,
					channel,
					eventName,
					payload: eventPayload,
				},
				context: {
					headers: {
						...headers,
						'x-forge-context-token': contextToken,
					},
				},
			});
		},
		[appId, contextAri, mutationFunction, headers],
	);
};
